import React from "react";
import { Clock, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Modal shown when the idle threshold's warning window kicks in.
 * The countdown runs while it's open; clicking "Stay signed in" or anywhere
 * else on the page (via the parent hook's activity listener) dismisses it.
 */
export default function IdleWarningModal({
  open,
  secondsLeft,
  onStay,
  onLogoutNow,
}) {
  if (!open) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const formatted = `${minutes}:${seconds.toString().padStart(2, "0")}`;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-zinc-900/60 backdrop-blur-sm p-4"
      data-testid="idle-warning-modal"
      role="alertdialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 border border-brand-100">
        <div className="flex items-start gap-3 mb-3">
          <div className="rounded-full bg-brand-50 p-2 mt-0.5">
            <Clock className="w-5 h-5 text-brand" />
          </div>
          <div>
            <div className="font-heading text-xl font-semibold tracking-tight text-zinc-900">
              You'll be signed out soon
            </div>
            <p className="text-sm text-zinc-600 mt-1">
              We haven't seen any activity for a while. To protect patient data,
              we'll sign you out automatically in{" "}
              <span
                className="font-mono font-semibold text-brand"
                data-testid="idle-countdown"
              >
                {formatted}
              </span>
              .
            </p>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
          <Button
            variant="ghost"
            onClick={onLogoutNow}
            className="text-zinc-600"
            data-testid="idle-logout-now-btn"
          >
            <LogOut className="w-4 h-4 mr-1.5" />
            Sign out now
          </Button>
          <Button
            onClick={onStay}
            className="bg-brand hover:bg-brand-dark text-white"
            data-testid="idle-stay-btn"
          >
            Stay signed in
          </Button>
        </div>
      </div>
    </div>
  );
}
